import React from 'react';
import styled from 'styled-components';
import { Spin, Button, Icon, Tooltip, Text, Flex } from '@bb/elements-web';
import type { IBillingProfileData, IKeyholderProfileData } from '@/app-package';
import { formatDateStrWithYearAndShortMonth } from '../../../utils/index';
import { renderCreditCardIcon, renderCreditCardName } from './CreditCardIcons';
import { CardErrorTag } from '../../pages/SubscriptionsPaymentsPage/components/CardErrorTag';
import { CanManageBillingProfileType } from '../../pages/SubscriptionsPaymentsPage/SubscriptionsPageContext';
import { useUpdatePaymentCardModalContext } from './UpdatePaymentCardModalContext';

interface IManagePaymentMethodCardProps {
  /** Callback function to handle click events on the card. */
  onUpdateCardClick: () => void;
  /** Whether to show a card error warning. */
  showCardErrorWarning: boolean;
  /** The billing profile data. */
  billingProfile?: IBillingProfileData | IKeyholderProfileData;
  /** Whether the billing profile is being fetched. */
  isFetchingProfile: boolean;
  /** The user's ability to manage billing related actions. */
  canManageBilling: CanManageBillingProfileType;
}

/**
 * UI that displays inside of the card for a user to add their credit card.
 */
const ManagePaymentMethodCard: React.FC<IManagePaymentMethodCardProps> = ({
  onUpdateCardClick,
  showCardErrorWarning,
  billingProfile,
  isFetchingProfile,
  canManageBilling,
}) => {
  const { updatedPaymentInfo } = useUpdatePaymentCardModalContext();

  const canManageBillingResponse = canManageBilling.response;
  const manageBillingMessage =
    'message' in canManageBilling ? canManageBilling.message : undefined;

  // Display the updated card info if it exists
  const ccType = updatedPaymentInfo?.ccType || billingProfile?.CCType;
  const maskedCCNumber =
    updatedPaymentInfo?.maskedCCNumber || billingProfile?.MaskedCCNumber;
  const ccExpiryDate =
    updatedPaymentInfo?.ccExpiryDate || billingProfile?.CCExpiryDate;

  if (isFetchingProfile) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{ width: '100%', padding: '8px 0' }}
      >
        <Spin dataCy="loading-card-spin" />
      </Flex>
    );
  }
  if (
    !billingProfile ||
    !billingProfile.CCType ||
    !billingProfile.MaskedCCNumber
  ) {
    return (
      <StyledContainer>
        <NoCardInfo>
          <Icon size={36} color="grey" icon="creditCardOutlined" />
          <NoCardText color="SECONDARY_TEXT">No credit card</NoCardText>
        </NoCardInfo>
        <Button
          onClick={onUpdateCardClick}
          type="secondary"
          size="small"
          minWidth="auto"
          disabled={!canManageBillingResponse}
          dataCy="no-card-update-button"
        >
          Update
        </Button>
      </StyledContainer>
    );
  }

  // What we return for the renderCardInfo
  return (
    <>
      <StyledCardInfoRow>
        <IconAndCardInfoText>
          {renderCreditCardIcon(ccType, 36)}
          <Flex vertical gap="8px">
            <Flex gap="16px" align="center">
              <Text thickness="bold" size="large">
                {renderCreditCardName(ccType)} ending in{' '}
                {maskedCCNumber.slice(-4)}
              </Text>
              {
                // If we are forcing the warning and they haven't entered new card info, add a warning tag
                showCardErrorWarning && <CardErrorTag>Card Error</CardErrorTag>
              }
            </Flex>
            <Text size="large" color="SECONDARY_TEXT">
              Expires {formatDateStrWithYearAndShortMonth(ccExpiryDate)}
            </Text>
          </Flex>
        </IconAndCardInfoText>
        <Tooltip title={canManageBillingResponse ? '' : manageBillingMessage}>
          <Button
            onClick={onUpdateCardClick}
            type="secondary"
            minWidth="auto"
            size="small"
            disabled={!canManageBillingResponse}
            dataCy="change-card-button"
            style={{ padding: '6px 16px' }}
          >
            Update
          </Button>
        </Tooltip>
      </StyledCardInfoRow>
    </>
  );
};

const IconAndCardInfoText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 24px;
`;

const NoCardText = styled(Text)`
  display: inline-flex;
  align-items: center;
`;

const NoCardInfo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 24px;
`;

const StyledCardInfoRow = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const StyledContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  gap: 24px;
  width: 100%;
`;

export default ManagePaymentMethodCard;
